import Dexie, { type EntityTable } from 'dexie'
import type { Edge, MetaRow, OpRecord, Resource } from './types'

// Client tables (spec §3.3). Every non-deleted resource lives here; deleted
// rows are kept 30 days for the Log and then purged.
export class Db extends Dexie {
  resources!: EntityTable<Resource, 'id'>
  edges!: EntityTable<Edge, 'from_id'>
  ops!: EntityTable<OpRecord, 'op_id'>
  meta!: EntityTable<MetaRow, 'key'>

  constructor(name = 'alfieos') {
    super(name)
    this.version(1).stores({
      resources: 'id, kind, [kind+status.state], spec.scheduled, spec.due, deleted_at, status.template_id',
      edges: '[from_id+to_id+relation], to_id, from_id',
      ops: 'op_id, created_at, state',
      meta: 'key',
    })
    // v3: workspaces partition by the reserved label `ws`
    this.version(2).stores({
      resources:
        'id, kind, [kind+status.state], spec.scheduled, spec.due, deleted_at, status.template_id, labels.ws, [labels.ws+kind]',
    })
  }

  async getMeta<T>(key: string): Promise<T | undefined> {
    const row = await this.meta.get(key)
    return row?.value as T | undefined
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    await this.meta.put({ key, value })
  }
}

export const META = {
  token: 'token',
  deviceId: 'device_id',
  deviceName: 'device_name',
  lastSeq: 'last_seq',
  bootstrappedAt: 'bootstrapped_at',
  serverTz: 'server_tz',
  serverToday: 'server_today',
  activeTask: 'active_task',
  picker: 'today_picker',
  splitDismissed: 'split_dismissed',
  reviewDismissedAt: 'review_dismissed_at',
  lastReviewAt: 'last_review_at',
  /** `<morning|recap>:<date>` when a day card was put off for the day */
  dayCardDismissed: 'day_card_dismissed',
  /** the build whose release note this device has already read */
  seenRelease: 'seen_release',
  pushSubscribed: 'push_subscribed',
  workspace: 'workspace',
  recentEmoji: 'recent_emoji',
  /** signed in with a workspace invitation rather than the owner's token (decided by the server) */
  member: 'member',
  memberGrant: 'member_grant',
  memberFrom: 'member_from',
  memberName: 'member_name',
  /** an admin member: the flag, and the id of her own Settings record */
  memberAdmin: 'member_admin',
  memberSettings: 'member_settings',
} as const

export const createDb = (name?: string) => new Db(name)
